import { get } from '../helpers/fetchServicesMethods';

/**
 * The saved views an alert can be pointed at: the built-in views, the user's own,
 * and views colleagues have shared with them, under the names Saved Views shows in
 * Discover.
 *
 * Kept separate from Discover's FieldConfig on purpose: that module is a singleton
 * the result browser mutates as the user works, and the Alert Setting dialog has no
 * business loading or disturbing it.
 */

/**
 * The built-in views, which are not saved views at all: Discover hands these keys
 * straight to switchView() and builds the columns itself, so they never appear in
 * result_rb2. Keys and labels mirror the Saved Views sidebar in Discover.js.
 * "Default Chemical Mod." is only offered for a CM result, exactly as the sidebar
 * gates it on isCmWorkflow.
 */
const BUILT_IN_VIEWS = [
    { value: 'default', label: 'Default View' },
    { value: 'defaultAli', label: 'Default Alignment' },
    { value: 'defaultFam', label: 'Default Patent Family' },
    { value: 'defaultFt', label: 'Default Full Text' }
];

const BUILT_IN_CM_VIEW = { value: 'defaultCm', label: 'Default Chemical Mod.' };

/**
 * A shared view is stored as "shared:<owner gq_user_id>:<name>". The name alone is not
 * enough: a colleague can share a view under the same name as one of the user's own,
 * or as another colleague's, and the backend would then resolve it to the wrong one.
 * Must match SHARED_ALERT_VIEW in FulltextUtils.java.
 */
const SHARED_VIEW_PREFIX = 'shared:';

function sharedAlertViewValue(ownerId, name) {
    return SHARED_VIEW_PREFIX + ownerId + ':' + name;
}

/**
 * How to name a stored alert view in a message: a shared view by its own name rather
 * than the stored form.
 */
function alertViewLabel(value) {
    const shared = /^shared:\d+:([\s\S]+)$/.exec(value || '');
    return shared ? shared[1] + ' (shared)' : value;
}

/**
 * The current user's own saved views, from the result_rb2 preference.
 *
 * `loaded` says whether the preference was actually read. "No saved views" and "could
 * not ask" produce the same empty object, and the caller must tell them apart: a view
 * missing from an incomplete list looks deleted, and treating it as deleted would
 * overwrite the owner's choice on the next save.
 *
 * @return { views, loaded } - views keyed by name
 */
async function loadOwnViews() {
    let apiUrl = 'do=gqpref.get&format=json&prefname=all&context=result_rb2';
    try {
        const response = await get(apiUrl);
        if (!response || response.response_status !== 0) {
            return { views: {}, loaded: false };
        }
        // A user who has never saved a view has no preference content at all: that is a
        // successful read of an empty set, not a failure.
        let content = response.response_content;
        if (!content) {
            return { views: {}, loaded: true };
        }
        if (typeof content === 'string') {
            content = JSON.parse(content);
        }
        return { views: content.views || {}, loaded: true };
    } catch (error) {
        console.log('could not load own views for alert setting', error);
        return { views: {}, loaded: false };
    }
}

/**
 * The views other users have shared with this user, with who shared each one.
 *
 * @return { views, loaded } - views are { name, ownerId, ownerName }
 */
async function loadSharedViews() {
    try {
        const response = await get('do=gqsharedview.shared_with_me&format=json');
        if (!response || response.response_status !== 0 || !response.response_content) {
            return { views: [], loaded: false };
        }
        const views = [];
        response.response_content.forEach(function (shared) {
            if (shared && shared.view_name && shared.owner_id) {
                views.push({ name: shared.view_name, ownerId: shared.owner_id, ownerName: shared.owner_name || '' });
            }
        });
        return { views: views, loaded: true };
    } catch (error) {
        console.log('could not load shared views for alert setting', error);
        return { views: [], loaded: false };
    }
}

/**
 * Every view the user can pick for an alert, as SelectBox items, in the order the
 * sidebar lists them: the built-in views, then the user's own, then views shared with
 * them.
 *
 * @param isCmResult true to offer the CM default, as Discover does for a CM result
 * @return { items, ownViewsLoaded, sharedViewsLoaded } - items are { value, label, builtIn },
 *  plus { shared, sharedName } for a shared view
 */
async function listAlertViews(isCmResult) {
    const items = BUILT_IN_VIEWS.concat(isCmResult ? [BUILT_IN_CM_VIEW] : []).map(function (view) {
        return { value: view.value, label: view.label, builtIn: true };
    });

    const [own, shared] = await Promise.all([loadOwnViews(), loadSharedViews()]);
    const ownViews = own.views;
    Object.keys(ownViews).sort().forEach(function (name) {
        // A saved view named after a built-in key would be indistinguishable from it
        // once stored as alert_view_name, so the built-in wins and the duplicate is
        // left out rather than offered twice.
        if (items.some(function (item) { return item.value === name; })) {
            return;
        }
        items.push({ value: name, label: name, builtIn: false });
    });

    // Then views shared with them, stored with their owner so that one shared under the
    // same name as one of the user's own still reaches the alert as itself.
    const sharedViews = shared.views.slice().sort(function (a, b) {
        return a.name < b.name ? -1 : (a.name > b.name ? 1 : 0);
    });
    sharedViews.forEach(function (view) {
        const value = sharedAlertViewValue(view.ownerId, view.name);
        if (items.some(function (item) { return item.value === value; })) {
            return;
        }
        // Two colleagues sharing the same name: say whose is whose.
        const sameName = sharedViews.filter(function (other) { return other.name === view.name; }).length > 1;
        items.push({
            value: value,
            label: view.name + (sameName && view.ownerName ? ' (shared by ' + view.ownerName + ')' : ' (shared)'),
            builtIn: false,
            shared: true,
            sharedName: view.name
        });
    });

    return { items: items, ownViewsLoaded: own.loaded, sharedViewsLoaded: shared.loaded };
}

/**
 * The item a stored alert view refers to, or undefined when none does.
 *
 * A shared view chosen before its owner was stored with it is a bare name, which the
 * backend resolves to the user's own view of that name first, then to one shared with
 * them. Matched the same way here, so the dialog shows what the alert really uses and
 * the next save stores the owner too. If several colleagues share that name there is
 * no telling which the alert meant, so the bare name comes back as an item of its own,
 * not yet in the list, and keeps resolving the old way.
 */
function findAlertViewItem(items, stored) {
    const exact = items.find(function (item) { return item.value === stored; });
    if (exact) {
        return exact;
    }
    const legacy = items.filter(function (item) { return item.shared && item.sharedName === stored; });
    if (legacy.length === 1) {
        return legacy[0];
    }
    if (legacy.length > 1) {
        return { value: stored, label: stored + ' (shared)', builtIn: false, shared: true, sharedName: stored };
    }
    return undefined;
}

/**
 * The alerts, if any, that report their new results with a given saved view. Used to
 * refuse deleting a view an alert still depends on: the alert stores the view by name,
 * so deleting it would leave the alert pointing at nothing.
 *
 * `checked` says whether the question was actually answered. A failed check is not the
 * same as "not in use", and the caller must not delete on the strength of it: deleting
 * a view cannot be undone, whereas being asked to try again costs nothing.
 *
 * @return { checked, inUse, alerts } - alerts is a list of alert titles
 */
async function alertViewUsage(viewName) {
    if (!viewName) {
        return { checked: true, inUse: false, alerts: [] };
    }
    let apiUrl = 'do=gqpref.alert_usage&format=json&prefname=' + encodeURIComponent(viewName);
    try {
        const response = await get(apiUrl);
        if (!response || response.response_status !== 0 || !response.response_content) {
            return { checked: false, inUse: false, alerts: [] };
        }
        const content = response.response_content;
        return {
            checked: true,
            inUse: Boolean(content.in_use),
            alerts: content.alerts || []
        };
    } catch (error) {
        console.log('could not check alert usage for view ' + viewName, error);
        return { checked: false, inUse: false, alerts: [] };
    }
}

const alertViews = {
    listAlertViews,
    findAlertViewItem,
    alertViewLabel,
    alertViewUsage
};

export default alertViews;
